/**
 * Family Sharing component for Pro users
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import familyService from '../services/familyService';
import SharedBoxes from './SharedBoxes';

const FamilySharing = ({ userSubscription }) => {
  const [familyInfo, setFamilyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  
  // Dialog states
  const [createFamilyOpen, setCreateFamilyOpen] = useState(false);
  const [inviteMemberOpen, setInviteMemberOpen] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  const isProUser = ['pro', 'organizer', 'mover_pro'].includes(userSubscription?.plan);

  useEffect(() => {
    if (isProUser) {
      loadFamilyInfo();
    } else {
      setLoading(false);
    }
  }, [isProUser]);

  const loadFamilyInfo = async () => {
    try {
      setLoading(true);
      const response = await familyService.getFamilyInfo();
      setFamilyInfo(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFamily = async () => {
    try {
      setError('');
      const response = await familyService.createFamily(familyName);
      setSuccess('Family created successfully!');
      setCreateFamilyOpen(false);
      setFamilyName('');
      loadFamilyInfo();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleInviteMember = async () => {
    try {
      setError('');
      if (!familyInfo?.family?.id) {
        setError('No family found');
        return;
      }
      
      const response = await familyService.inviteMember(
        familyInfo.family.id,
        inviteEmail,
        inviteRole
      );
      setSuccess('Invitation sent successfully!');
      setInviteMemberOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      loadFamilyInfo();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveMember = async (memberId, memberEmail) => {
    if (!window.confirm(`Are you sure you want to remove ${memberEmail} from the family?`)) {
      return;
    }
    
    try {
      setError('');
      await familyService.removeMember(familyInfo.family.id, memberId);
      setSuccess('Member removed successfully!');
      loadFamilyInfo();
    } catch (err) {
      setError(err.message);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner': return 'primary';
      case 'admin': return 'secondary';
      case 'member': return 'default';
      case 'viewer': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  if (!isProUser) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Family Sharing
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Family sharing is available with Pro subscription. Upgrade to invite up to 4 family members and share your inventory.
          </Alert>
          <Button variant="contained" color="primary">
            Upgrade to Pro
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Family Sharing
          </Typography>
          {!familyInfo?.family && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateFamilyOpen(true)}
            >
              Create Family
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {familyInfo?.family ? (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {familyInfo.family.name}
              </Typography>
            </Box>

            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ mb: 2 }}>
              <Tab icon={<PeopleIcon />} label="Members" />
              <Tab icon={<InventoryIcon />} label="Shared Boxes" />
            </Tabs>

            {currentTab === 0 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2" color="textSecondary">
                    Members ({familyInfo.members?.length || 0}/4)
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteMemberOpen(true)}
                    disabled={familyInfo.members?.length >= 4}
                  >
                    Invite Member
                  </Button>
                </Box>

                <List>
                  {familyInfo.members?.map((member) => (
                    <ListItem key={member.id}>
                      <ListItemText
                        primary={member.email}
                        secondary={
                          <span style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <Chip
                              label={member.role}
                              size="small"
                              color={getRoleColor(member.role)}
                            />
                            <Chip
                              label={member.status}
                              size="small"
                              color={getStatusColor(member.status)}
                            />
                            {member.joined_at && (
                              <Typography variant="caption" component="span">
                                Joined {new Date(member.joined_at).toLocaleDateString()}
                              </Typography>
                            )}
                          </span>
                        }
                      />
                      {member.role !== 'owner' && (
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={() => handleRemoveMember(member.id, member.email)}
                            title="Remove member"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {currentTab === 1 && (
              <SharedBoxes />
            )}
          </Box>
        ) : (
          <Alert severity="info">
            Create a family to start sharing your inventory with family members.
          </Alert>
        )}

        {/* Create Family Dialog */}
        <Dialog open={createFamilyOpen} onClose={() => setCreateFamilyOpen(false)}>
          <DialogTitle>Create Family</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Family Name"
              fullWidth
              variant="outlined"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="e.g., Smith Family"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateFamilyOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateFamily}
              variant="contained"
              disabled={!familyName.trim()}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invite Member Dialog */}
        <Dialog open={inviteMemberOpen} onClose={() => setInviteMemberOpen(false)}>
          <DialogTitle>Invite Family Member</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="member@example.com"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth variant="outlined">
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                label="Role"
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteMemberOpen(false)}>Cancel</Button>
            <Button
              onClick={handleInviteMember}
              variant="contained"
              disabled={!inviteEmail.trim()}
            >
              Send Invitation
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default FamilySharing;
